import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtEur, errDetail } from "../lib/api";
import { useApp } from "../lib/store";
import { normalizeLine } from "../lib/fields";
import { EntityForm } from "./EntityForm";
import { CommentsAudit } from "./CommentsAudit";
import { LineItemsEditor } from "./LineItemsEditor";
import { RfqDocuments } from "./RfqDocuments";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Flag, Factory, CheckCircle2, XCircle, Save, FileText } from "lucide-react";

/**
 * Full opportunity record view (details, RFQ documents, line items, quote generation,
 * production-site match, close workflow, comments/audit). Reusable so opportunities can be
 * opened in place on both the Opportunities page and the Account 360° page.
 */
export function OpportunityDetailDialog({ opportunity, onClose, onChanged }) {
  const { t, canWrite, plLabel, plOptions } = useApp();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(opportunity);
  const [detailLines, setDetailLines] = useState([]);
  const [editing, setEditing] = useState(false);
  const [closing, setClosing] = useState(null);
  const [closeReason, setCloseReason] = useState("");
  const [match, setMatch] = useState(null);
  const [matching, setMatching] = useState(false);

  const refresh = useCallback(async () => {
    try { const r = await api.get(`/opportunities/${opportunity.id}`); setDetail(r.data); }
    catch (e) { toast.error(errDetail(e)); }
  }, [opportunity.id]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { setDetailLines(((detail && detail.line_items) || []).map(normalizeLine)); }, [detail]);

  const notifyChanged = () => onChanged && onChanged();

  const saveDetailLines = async () => {
    try {
      const r = await api.put(`/opportunities/${detail.id}`, { ...detail, line_items: detailLines });
      setDetail(r.data); toast.success(t("save") + " ✓"); notifyChanged();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const generateQuote = async () => {
    try {
      const r = await api.post(`/opportunities/${detail.id}/create-quote`);
      toast.success(t("quote_created") + `: ${r.data.quote_number}`);
      navigate(`/quotes/${r.data.id}`);
    } catch (e) { toast.error(errDetail(e)); }
  };

  const loadMatch = async () => {
    setMatching(true);
    try { const r = await api.get(`/opportunities/${detail.id}/site-match`); setMatch(r.data); }
    catch (e) { toast.error(errDetail(e)); }
    finally { setMatching(false); }
  };

  const confirmClose = async () => {
    try {
      await api.put(`/opportunities/${detail.id}/stage`, { stage: closing.stage, close_reason: closeReason });
      toast.success(`${detail.name} → ${plLabel("opp_stages", closing.stage)}`);
      setClosing(null); setCloseReason("");
      notifyChanged(); onClose();
    } catch (e) { toast.error(errDetail(e)); }
  };

  if (!detail) return null;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="opp-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {detail.aging_flag && <Flag className="h-4 w-4 text-red-500" />}{detail.name}
          </DialogTitle>
          <DialogDescription>{detail.account_name}</DialogDescription>
        </DialogHeader>

        {editing ? (
          <EntityForm module="opportunities" initial={detail}
            onSaved={async () => { setEditing(false); await refresh(); notifyChanged(); }}
            onCancel={() => setEditing(false)} />
        ) : (<>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {[["account", detail.account_name], ["stage", plLabel("opp_stages", detail.stage)], ["amount", fmtEur(detail.amount, detail.currency)],
              ["probability", `${detail.probability}%`], ["weighted_forecast", fmtEur(detail.weighted_amount)],
              ["close_date", detail.close_date], ["product_type", detail.product_type],
              ["print_technology", detail.print_technology], ["substrate", detail.substrate],
              ["finishing", (detail.finishing || []).join(", ")],
              ["annual_volume", `${(detail.annual_volume || 0).toLocaleString("de-DE")} ${detail.volume_unit || ""}`],
              ["num_skus", detail.num_skus], ["num_colors", detail.num_colors],
              ["business_type", detail.business_type], ["competitors", (detail.competitors || []).join(", ")],
              ["close_reason", detail.close_reason || "–"], ["owner", detail.owner_name]].map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-gray-50 py-1">
                <span className="text-gray-500">{t(k)}</span><span className="font-medium text-right">{v || "–"}</span>
              </div>
            ))}
          </div>
          {canWrite && (
            <div className="flex flex-wrap gap-2 pt-2">
              <Button size="sm" variant="outline" data-testid="opp-detail-edit-btn" onClick={() => setEditing(true)}>{t("edit")}</Button>
              {!detail.stage?.startsWith("Closed") && (<>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="opp-close-won-btn"
                  onClick={() => setClosing({ stage: "Closed Won" })}>{t("mark_won")}</Button>
                <Button size="sm" variant="destructive" data-testid="opp-close-lost-btn"
                  onClick={() => setClosing({ stage: "Closed Lost" })}>{t("mark_lost")}</Button>
              </>)}
            </div>
          )}
          <div className="pt-3 border-t border-gray-100">
            <RfqDocuments module="opportunities" itemId={detail.id} canWrite={canWrite}
              onAddLines={(newLines) => setDetailLines((ls) => [...ls, ...newLines])} />
          </div>
          <div className="pt-3 border-t border-gray-100">
            <LineItemsEditor lines={detailLines} setLines={setDetailLines} canWrite={canWrite} currency={detail.currency || "EUR"} />
            {canWrite && (
              <div className="flex flex-wrap gap-2 mt-4">
                <Button size="sm" className="bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" data-testid="opp-lines-save-btn" onClick={saveDetailLines}>
                  <Save className="h-3.5 w-3.5 mr-1.5" />{t("save")}
                </Button>
                <Button size="sm" className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold" data-testid="opp-generate-quote-btn" onClick={generateQuote}>
                  <FileText className="h-3.5 w-3.5 mr-1.5" />{t("generate_quote")}
                </Button>
              </div>
            )}
          </div>
          <div className="pt-2 border-t border-gray-100">
            <Button size="sm" variant="outline" data-testid="opp-site-match-btn" disabled={matching} onClick={loadMatch}>
              <Factory className="h-3.5 w-3.5 mr-1.5" />{matching ? "…" : t("suggest_site")}
            </Button>
            {match && (
              <div className="mt-3 space-y-2" data-testid="site-match-results">
                {match.matches.map((m, i) => (
                  <div key={m.plant_id} className={`border rounded-md p-3 ${i === 0 ? "border-[#F5A623] bg-amber-50/50" : "border-gray-100"}`} data-testid={`site-match-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold flex items-center gap-1.5">
                        <Factory className="h-3.5 w-3.5 text-gray-400" />{m.plant}
                        <span className="text-xs font-normal text-gray-400">{m.location}</span>
                      </span>
                      <span className={`text-sm font-mono font-bold ${m.score >= 70 ? "text-emerald-600" : m.score >= 40 ? "text-amber-600" : "text-red-500"}`}>{m.score}/100</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{t("best_press")}: {m.press} · {m.web_width} mm</p>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {m.matched.map((x) => (
                        <span key={x} className="text-[10px] bg-emerald-50 text-emerald-700 rounded-full px-2 py-0.5 flex items-center gap-1">
                          <CheckCircle2 className="h-2.5 w-2.5" />{x}
                        </span>
                      ))}
                      {m.missing.map((x) => (
                        <span key={x} className="text-[10px] bg-red-50 text-red-600 rounded-full px-2 py-0.5 flex items-center gap-1">
                          <XCircle className="h-2.5 w-2.5" />{x}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <CommentsAudit module="opportunities" id={detail.id} />
        </>)}
      </DialogContent>

      <Dialog open={!!closing} onOpenChange={(o) => !o && setClosing(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{plLabel("opp_stages", closing?.stage)} – {detail.name}</DialogTitle>
            <DialogDescription>{t("required_close_reason")}</DialogDescription>
          </DialogHeader>
          <Select value={closeReason} onValueChange={setCloseReason}>
            <SelectTrigger data-testid="close-reason-select"><SelectValue placeholder={t("close_reason")} /></SelectTrigger>
            <SelectContent>{plOptions("close_reasons").map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
          <Button className="bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" disabled={!closeReason} data-testid="close-confirm-btn" onClick={confirmClose}>
            {t("save")}
          </Button>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
